import { createContext, ReactNode, useContext, useState } from "react";
import esMX from "../locales/es_MX.json";
import enUS from "../locales/en_US.json";

export type Locale = "es_MX" | "en_US";

interface Dictionary {
  [key: string]: string | Dictionary;
}

type LanguageContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
};

const STORAGE_KEY = "portfolio_locale";

const dictionaries: Record<Locale, Dictionary> = {
  es_MX: esMX as Dictionary,
  en_US: enUS as Dictionary,
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

const isLocale = (value: string): value is Locale =>
  value === "es_MX" || value === "en_US";

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (typeof window === "undefined") {
      return "es_MX";
    }

    const storedLocale = localStorage.getItem(STORAGE_KEY);
    return storedLocale && isLocale(storedLocale) ? storedLocale : "es_MX";
  });

  const setLocale = (nextLocale: Locale) => {
    setLocaleState(nextLocale);
    localStorage.setItem(STORAGE_KEY, nextLocale);
  };

  const t = (key: string) => {
    const result = key.split(".").reduce<string | Dictionary | undefined>(
      (acc, part) => {
        if (!acc || typeof acc === "string") {
          return undefined;
        }
        return acc[part];
      },
      dictionaries[locale]
    );

    return typeof result === "string" ? result : key;
  };

  const value: LanguageContextValue = {
    locale,
    setLocale,
    t,
  };

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};
